import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StockThumb from "@/components/StockThumb";
import DepartmentChips, {
  DepartmentBadges,
} from "@/components/DepartmentChips";
import MaterialChips from "@/components/MaterialChips";
import SupplierChips from "@/components/SupplierChips";
import CategoryChips from "@/components/CategoryChips";
import { Search, Info, ClipboardCheck, Save, Camera, Loader2, Printer } from "lucide-react";
import { fmtQty } from "@/lib/pack";

/**
 * Post-stocktake results wizard (manager-only).
 *
 * Workflow: after the paper stocktake sheets come back with pencilled-in
 * counts, the manager types the counted numbers here. For each item, if
 * the counted value differs from the on-record `current_qty`, we POST an
 * IN (found stock) or OUT (shrinkage) movement flagged `stocktake=true`.
 * Reports exclude these movements by default so consumption trends stay
 * clean; the /reports "Include stocktake" toggle can flip them back in
 * when auditing.
 *
 * Draft state is local — nothing is written until the user clicks "Post
 * counts". Bulk POST goes to /api/stocktake/apply.
 */
const StocktakePost = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [material, setMaterial] = useState("all");
  const [category, setCategory] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [drafts, setDrafts] = useState({}); // id -> string (raw input)
  const [busy, setBusy] = useState(false);
  // Baseline mode: when toggled on, the resulting variance movements are
  // flagged is_baseline=true on the backend so they're excluded from the
  // Shrinkage report. Used on go-live day when we're snapping to physical
  // reality after a period of untracked usage.
  const [baselineMode, setBaselineMode] = useState(false);
  // Progress filter — when true, hides rows that already have a valid count
  // typed. Lets managers focus on what's left to walk through so nothing
  // gets missed on a big stocktake.
  const [onlyUncounted, setOnlyUncounted] = useState(false);
  // Tab: "todo" = items not yet counted today, "done" = items counted today
  // (from db `last_stocktake_at`). Managers walk the "todo" tab first, then
  // check the "done" tab to verify what's been captured — with timestamps
  // so it's obvious when each item was signed off.
  const [tab, setTab] = useState("todo");
  // OCR photo-import state — file picker + Gemini call + confirm review.
  const [ocrBusy, setOcrBusy] = useState(false);
  const fileInputRef = React.useRef(null);

  const handlePhotoUpload = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const okTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Please pick a JPEG, PNG or WEBP photo");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo must be under 8 MB — try a lower-res shot");
      return;
    }
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/stocktake/parse-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const parsed = data.parsed || [];
      if (parsed.length === 0) {
        toast.error(
          "No matching items found — check the sheet is legible and matches your catalog",
        );
        return;
      }
      setDrafts((prev) => {
        const next = { ...prev };
        parsed.forEach((r) => {
          next[r.stock_id] = String(r.counted_qty);
        });
        return next;
      });
      toast.success(
        `Read ${parsed.length} count${parsed.length === 1 ? "" : "s"} from photo — review then Post`,
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not read the photo — try a clearer shot",
      );
    } finally {
      setOcrBusy(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/stock");
      setItems(data);
    } catch {
      toast.error("Could not load catalog");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setDraft = (id, value) =>
    setDrafts((prev) => ({ ...prev, [id]: value }));

  const relevantMaterials = useMemo(() => {
    if (dept === "all") return [];
    const inScope = items.filter((it) => {
      const list = it.departments?.length
        ? it.departments
        : [it.department].filter(Boolean);
      return list.includes(dept);
    });
    return Array.from(new Set(inScope.map((it) => it.material).filter(Boolean)));
  }, [items, dept]);

  const relevantSuppliers = useMemo(() => {
    const inScope = items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length
          ? it.departments
          : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      return true;
    });
    return Array.from(
      new Set(inScope.map((it) => it.supplier_default).filter(Boolean)),
    );
  }, [items, dept, material]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length
          ? it.departments
          : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      if (
        supplierFilter !== "all" &&
        (it.supplier_default || "") !== supplierFilter
      )
        return false;
      if (!q) return true;
      return (
        it.name.toLowerCase().includes(q) ||
        (it.sku || "").toLowerCase().includes(q) ||
        (it.supplier_default || "").toLowerCase().includes(q) ||
        (it.material || "").toLowerCase().includes(q) ||
        (it.category || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, dept, material, category, supplierFilter]);

  // A row is "counted" once it has a valid non-negative number in the input,
  // whether that value matches on-record qty or differs. This is separate
  // from `entries` (which only lists deltas). Progress tracker uses this set
  // so managers can see how far through the physical walk they are.
  const isCounted = useCallback(
    (id) => {
      const raw = drafts[id];
      if (raw === undefined || raw === "") return false;
      const n = Number(raw);
      return !Number.isNaN(n) && n >= 0;
    },
    [drafts],
  );
  // "Counted today" combines two signals:
  //   1. In-memory drafts the manager is still typing (session-only)
  //   2. Persistent `last_stocktake_at` on the stock doc, from a prior POST
  // The persistent side is what solves the "posted but still on list" bug:
  // once submit() clears drafts, the item still moves into the Done tab
  // because its timestamp landed within the last 24h.
  const isSameDayLocal = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  };
  const countedToday = useCallback(
    (it) => isCounted(it.id) || isSameDayLocal(it.last_stocktake_at),
    [isCounted],
  );
  const countedCount = useMemo(
    () => items.filter((it) => countedToday(it)).length,
    [items, countedToday],
  );

  // Apply the "only uncounted" toggle on top of the base filter so search
  // + chip filters continue to work as expected while narrowing to work
  // that's still to be done.
  const visibleRows = useMemo(() => {
    // Tab always splits: Todo = not-yet-POSTED today (persistent), Done =
    // posted today. We deliberately look at `last_stocktake_at` here (not
    // `countedToday`) so a row does NOT vanish from Todo the instant a
    // draft digit is typed — that used to yank the input out from under
    // the manager mid-typing (e.g. couldn't get past "1" when entering
    // "12"). Drafts are still tracked separately for progress + colour.
    let base;
    if (tab === "done") {
      base = filtered.filter((it) => isSameDayLocal(it.last_stocktake_at));
    } else {
      base = filtered.filter((it) => !isSameDayLocal(it.last_stocktake_at));
    }
    // "Show uncounted only" is now redundant with the Todo tab, but keep it
    // for parity — inside Todo it hides rows the manager has typed but
    // hasn't posted, i.e. a way to zero in on what's still to walk.
    if (tab === "todo" && onlyUncounted) {
      base = base.filter((it) => !isCounted(it.id));
    }
    return base;
  }, [filtered, tab, onlyUncounted, isCounted]);

  // Entries that will actually generate a movement (i.e. counted differs
  // from current). Drives the "adjustments pending" strip and colours.
  const entries = useMemo(() => {
    const out = [];
    for (const it of items) {
      const raw = drafts[it.id];
      if (raw === undefined || raw === "") continue;
      const counted = Number(raw);
      if (Number.isNaN(counted) || counted < 0) continue;
      const current = Number(it.current_qty || 0);
      if (Math.abs(counted - current) < 1e-9) continue;
      out.push({ id: it.id, name: it.name, current, counted });
    }
    return out;
  }, [items, drafts]);

  // Everything the user has typed a valid count against — includes matches.
  // We POST this whole set so the backend can stamp last_stocktake_at on
  // every counted item, even the ones with no change. The Post button uses
  // this length so managers can sign off shelves that matched too.
  const allCounted = useMemo(() => {
    const out = [];
    for (const it of items) {
      const raw = drafts[it.id];
      if (raw === undefined || raw === "") continue;
      const counted = Number(raw);
      if (Number.isNaN(counted) || counted < 0) continue;
      out.push({ id: it.id, counted });
    }
    return out;
  }, [items, drafts]);

  const totals = useMemo(() => {
    let inRows = 0;
    let outRows = 0;
    for (const e of entries) {
      if (e.counted > e.current) inRows += 1;
      else outRows += 1;
    }
    return { inRows, outRows };
  }, [entries]);

  const submit = async () => {
    if (allCounted.length === 0) {
      toast.error("Type at least one count first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/stocktake/apply", {
        entries: allCounted.map((e) => ({
          stock_id: e.id,
          counted_qty: e.counted,
        })),
        baseline: baselineMode,
      });
      const created = (data.created || []).length;
      const stampedCount = allCounted.length;
      toast.success(
        `${baselineMode ? "Baseline set" : "Posted"} · ${stampedCount} item${
          stampedCount === 1 ? "" : "s"
        } signed off · ${created} adjustment${created === 1 ? "" : "s"}`,
      );
      setDrafts({});
      await load();
      window.dispatchEvent(new CustomEvent("stock-updated"));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not post stocktake results",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="stocktake-post-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="label-eyebrow">Post physical count results</div>
          <h1 className="font-display text-4xl md:text-5xl leading-none mt-2 flex items-center gap-3">
            <ClipboardCheck className="inline-block" size={40} strokeWidth={2.5} />
            Stocktake
          </h1>
        </div>
        <div className="flex items-start gap-3 flex-wrap">
          <a
            href="/stocktake-add-item-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stocktake-print-add-item-btn"
            className="nb-btn bg-white px-3 py-2 flex items-center gap-2 no-underline text-sm font-bold"
            title="Print a blank form staff can fill in for any items they find that aren't already in the app"
          >
            <Printer size={14} strokeWidth={2.5} /> Print add-item form
          </a>
          <div className="text-sm text-[#6B6B6B] max-w-sm flex items-start gap-2">
            <Info size={16} strokeWidth={2.5} className="mt-0.5 shrink-0" />
            <span>
              Type each item&apos;s <b>counted qty</b>, or import from a photo
              of the paper stocktake sheet. Any difference generates a
              stocktake-flagged adjustment so consumption charts stay clean.
            </span>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        capture="environment"
        className="hidden"
        data-testid="stocktake-photo-input"
        onChange={handlePhotoUpload}
      />

      {/* Baseline mode toggle — go-live day / post-hiatus reset. Emits
          is_baseline=true on movements so Shrinkage report ignores the
          variance. Prominent banner appears when armed. */}
      <div
        className={`nb-card p-3 mb-4 flex items-center gap-3 flex-wrap ${
          baselineMode ? "border-[3px] border-[#8A1F1F] bg-[#FFF7D6]" : ""
        }`}
        data-testid="baseline-mode-card"
      >
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          data-testid="baseline-mode-toggle-label"
        >
          <input
            type="checkbox"
            data-testid="baseline-mode-toggle"
            checked={baselineMode}
            onChange={(e) => setBaselineMode(e.target.checked)}
            className="w-5 h-5 accent-[#8A1F1F]"
          />
          <span className="font-bold text-base">Baseline mode</span>
        </label>
        <span className="text-xs text-[#607087] flex-1">
          Use on go-live day (or after any period of untracked usage) to snap
          on-record qty to physical reality — the variance <b>won&apos;t</b>{" "}
          show as shrinkage. All Spend history is preserved.
        </span>
        {baselineMode && (
          <div
            data-testid="baseline-mode-banner"
            className="bg-[#8A1F1F] text-white border-2 border-black rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-2"
          >
            <Info size={12} strokeWidth={2.5} />
            BASELINE ARMED — the next Post will NOT count as shrinkage
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={ocrBusy}
        data-testid="stocktake-photo-btn"
        className="nb-btn bg-white px-4 py-3 flex items-center gap-2 mb-4 disabled:opacity-60"
      >
        {ocrBusy ? (
          <Loader2 size={18} strokeWidth={2.5} className="animate-spin" />
        ) : (
          <Camera size={18} strokeWidth={2.5} />
        )}
        {ocrBusy ? "Reading photo…" : "Import counts from photo"}
      </button>

      <div className="relative mb-4">
        <Search
          size={20}
          strokeWidth={2.5}
          className="absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          data-testid="stocktake-search-input"
          className="nb-input pl-12 h-12 text-base"
          placeholder="Search catalog…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <DepartmentChips
          value={dept}
          onChange={setDept}
          testidPrefix="stocktake-dept"
        />
      </div>
      <div className="mb-3">
        <MaterialChips
          value={material}
          onChange={setMaterial}
          testidPrefix="stocktake-mat"
          relevantNames={relevantMaterials}
        />
      </div>
      <div className="mb-3">
        <CategoryChips
          value={category}
          onChange={setCategory}
          testidPrefix="stocktake-cat"
        />
      </div>
      <div className="mb-4">
        <SupplierChips
          value={supplierFilter}
          onChange={setSupplierFilter}
          testidPrefix="stocktake-supplier"
          relevantNames={relevantSuppliers}
        />
      </div>

      {/* Tabs — split "still to count" from "counted today" so a huge
          walk doesn't cause managers to re-count items they've already
          signed off. Numbers on each tab drive from persistent
          last_stocktake_at, so re-loading the page keeps the split. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="stocktake-tabs">
        <button
          type="button"
          data-testid="stocktake-tab-todo"
          onClick={() => setTab("todo")}
          className={`nb-btn px-4 py-2 text-sm font-bold ${
            tab === "todo" ? "nb-btn-primary" : "bg-white"
          }`}
        >
          Still to count · {items.length - countedCount}
        </button>
        <button
          type="button"
          data-testid="stocktake-tab-done"
          onClick={() => setTab("done")}
          className={`nb-btn px-4 py-2 text-sm font-bold ${
            tab === "done" ? "nb-btn-primary" : "bg-white"
          }`}
        >
          Counted today · {countedCount}
        </button>
      </div>

      {/* Sticky summary + submit bar */}
      <div
        className="nb-card p-3 mb-4 flex items-center justify-between gap-3 sticky top-2 z-10 bg-white flex-wrap"
        data-testid="stocktake-summary"
      >
        <div className="text-sm flex items-center gap-3 flex-wrap">
          <span data-testid="stocktake-progress">
            <b>{countedCount}</b> of <b>{items.length}</b> items counted
            {items.length > 0 && (
              <span className="text-[#6B6B6B]">
                {" "}
                ({Math.round((countedCount / items.length) * 100)}%)
              </span>
            )}
          </span>
          <span className="text-[#6B6B6B]">·</span>
          <span>
            <b>{entries.length}</b> adjustment
            {entries.length === 1 ? "" : "s"} pending
            {entries.length > 0 && (
              <span className="text-[#6B6B6B]">
                {" "}
                (
                <span className="text-[#2E7D32] font-bold">
                  {totals.inRows}
                </span>{" "}
                found ·{" "}
                <span className="text-[#8A1F1F] font-bold">
                  {totals.outRows}
                </span>{" "}
                shrinkage)
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label
            data-testid="stocktake-only-uncounted-label"
            className={`nb-btn px-3 py-2 flex items-center gap-2 cursor-pointer text-xs ${
              onlyUncounted ? "nb-btn-primary" : "bg-white"
            }`}
            title="Hide items you've already counted so nothing gets missed"
          >
            <input
              type="checkbox"
              data-testid="stocktake-only-uncounted"
              className="accent-black"
              checked={onlyUncounted}
              onChange={(e) => setOnlyUncounted(e.target.checked)}
            />
            Show uncounted only
          </label>
          <button
            type="button"
            onClick={submit}
            disabled={busy || allCounted.length === 0}
            data-testid="stocktake-post-btn"
            className={`nb-btn px-4 py-2 flex items-center gap-2 disabled:opacity-60 ${
              baselineMode ? "bg-[#8A1F1F] text-white" : "nb-btn-primary"
            }`}
          >
            <Save size={16} strokeWidth={2.5} />
            {busy
              ? "Posting…"
              : baselineMode
              ? `Post as BASELINE · ${allCounted.length || 0}`
              : `Post ${allCounted.length || ""} count${allCounted.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>

      <div
        className="grid grid-cols-1 xl:grid-cols-2 gap-3"
        data-testid="stocktake-grid"
      >
        {visibleRows.length === 0 && (
          <div className="nb-card p-8 text-center col-span-full text-[#6B6B6B]">
            {onlyUncounted && countedCount === items.length
              ? "🎉 Every item has been counted."
              : "No matching items — clear filters or search."}
          </div>
        )}
        {visibleRows.map((it) => {
          const raw = drafts[it.id] ?? "";
          const current = Number(it.current_qty || 0);
          const counted = raw === "" ? null : Number(raw);
          const invalid = counted !== null && (Number.isNaN(counted) || counted < 0);
          const delta =
            counted !== null && !invalid ? counted - current : null;
          const rowCounted = isCounted(it.id);
          return (
            <div
              key={it.id}
              className={`nb-card p-3 flex items-center gap-3 ${
                rowCounted ? "bg-[#F4FBF5]" : ""
              }`}
              data-testid={`stocktake-row-${it.id}`}
              data-counted={rowCounted ? "1" : "0"}
            >
              <StockThumb item={it} size="h-14 w-14" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-base leading-tight truncate">
                  {it.name}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  On record: <b>{fmtQty(current)}</b> {it.unit}
                  {it.material && ` · ${it.material}`}
                </div>
                {isSameDayLocal(it.last_stocktake_at) && (
                  <div
                    className="text-[10px] font-bold text-[#0B6B2A] mt-0.5 flex items-center gap-1"
                    data-testid={`stocktake-counted-at-${it.id}`}
                  >
                    ✓ Counted{" "}
                    {new Date(it.last_stocktake_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {it.last_stocktake_by ? ` · ${it.last_stocktake_by}` : ""}
                    {typeof it.last_stocktake_qty === "number"
                      ? ` · @ ${fmtQty(it.last_stocktake_qty)} ${it.unit}`
                      : ""}
                  </div>
                )}
                <div className="mt-1">
                  <DepartmentBadges
                    departments={
                      it.departments?.length
                        ? it.departments
                        : it.department
                        ? [it.department]
                        : []
                    }
                  />
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0 w-[140px]">
                <input
                  type="number"
                  step="any"
                  min="0"
                  className={`nb-input h-11 w-full text-right font-mono-num ${
                    invalid ? "border-[#8A1F1F]" : ""
                  }`}
                  data-testid={`stocktake-count-${it.id}`}
                  placeholder="Count"
                  value={raw}
                  onChange={(e) => setDraft(it.id, e.target.value)}
                />
                {rowCounted && delta === 0 && (
                  <div
                    className="text-xs font-bold text-[#2E7D32] flex items-center gap-1"
                    data-testid={`stocktake-match-${it.id}`}
                  >
                    ✓ matches on record
                  </div>
                )}
                {delta !== null && delta !== 0 && (
                  <div
                    className={`text-xs font-bold ${
                      delta > 0 ? "text-[#2E7D32]" : "text-[#8A1F1F]"
                    }`}
                    data-testid={`stocktake-delta-${it.id}`}
                  >
                    {delta > 0 ? "+" : ""}
                    {fmtQty(delta)} {it.unit}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StocktakePost;
